/**
 * Netvisor accounting (#3022): the workshop's own Netvisor company, customers,
 * products and sales invoices out, invoice and payment state back.
 *
 * OFF until the workshop enters its integration user, key and Y-tunnus,
 * maps the Netvisor VAT codes it invoices with, passes the connection test and
 * switches the integration on. Only invoices finalized AFTER that moment are
 * exported; history and invoices imported from another system never are. The
 * key is write-only and the user id is shown masked; the API returns neither.
 *
 * Anything Netvisor refused, or whose outcome is unknown, is listed with the
 * decisions a person may take. Nothing resends an unknown outcome by itself
 * without first looking it up in Netvisor.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import { ApiError } from '@/lib/api';
import { describeApiError } from '@/lib/describeApiError';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { formatCentsForDisplay } from '@/utils/money';
import {
  NETVISOR_LINE_KINDS,
  useDeleteNetvisorCredentials,
  useNetvisorIntegration,
  useResolveNetvisorItem,
  useSaveNetvisorCredentials,
  useSaveNetvisorSettings,
  useSetNetvisorEnabled,
  useTestNetvisorCredentials,
  type NetvisorAttentionItem,
  type NetvisorInvoiceStatus,
  type NetvisorLanguage,
  type NetvisorLineKind,
  type NetvisorProductConfig,
  type NetvisorSettings,
  type NetvisorSettingsInput,
} from './useNetvisorIntegration';

const ORGANISATION_PATTERN = /^\d{7}-\d$/;
const VAT_RATE_PATTERN = /^\d{1,2}([.,]\d{1,2})?$/;
const VAT_CODE_PATTERN = /^[A-Za-z0-9]{2,12}$/;
const NETVISOR_KEY_PATTERN = /^\d{1,18}$/;

const LANGUAGES: readonly NetvisorLanguage[] = ['FI', 'EN', 'SE'];
/** Endonyms: not translated (CLAUDE.md, language menus). */
const LANGUAGE_NAMES: Record<NetvisorLanguage, string> = { FI: 'Suomi', EN: 'English', SE: 'Svenska' };
const INVOICE_STATUSES: readonly NetvisorInvoiceStatus[] = ['open', 'unsent'];

const ERROR_KEYS: Record<string, string> = {
  NETVISOR_ENCRYPTION_KEY_MISSING: 'errors.encryptionMissing',
  NETVISOR_NOT_CONFIGURED: 'errors.notConfigured',
  NETVISOR_CUSTOMER_ID_REQUIRED: 'errors.customerIdRequired',
  NETVISOR_CUSTOMER_KEY_REQUIRED: 'errors.customerKeyRequired',
  NETVISOR_ORGANISATION_REQUIRED: 'errors.organisationRequired',
  NETVISOR_ORGANISATION_LOCKED: 'errors.organisationLocked',
  NETVISOR_CREDENTIALS_MISSING: 'errors.credentialsMissing',
  NETVISOR_NOT_READY: 'errors.notReady',
  NETVISOR_OUTBOUND_BLOCKED: 'errors.outboundBlocked',
  NETVISOR_ITEM_NOT_FOUND: 'errors.itemNotFound',
  NETVISOR_CONFIRMATION_REQUIRED: 'errors.confirmationRequired',
  NETVISOR_KEY_IN_USE: 'errors.keyInUse',
  NETVISOR_KEY_REQUIRED: 'errors.keyRequired',
  NETVISOR_ALREADY_IN_NETVISOR: 'errors.alreadyInNetvisor',
  NETVISOR_DECISION_NOT_SUPPORTED: 'errors.decisionNotSupported',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  NETVISOR_TEST_UNAUTHORIZED: 'errors.testUnauthorized',
  NETVISOR_TEST_REJECTED: 'errors.testRejected',
  NETVISOR_TEST_UNAVAILABLE: 'errors.testUnavailable',
  NETVISOR_TEST_UNDECRYPTABLE: 'errors.testUndecryptable',
};

const MISSING_KEYS: Record<string, string> = {
  credentials: 'missing.credentials',
  vat_codes: 'missing.vatCodes',
  product_group: 'missing.productGroup',
  test: 'missing.test',
};

const KIND_KEYS: Record<NetvisorLineKind, string> = {
  labor: 'kinds.labor',
  part: 'kinds.part',
  service: 'kinds.service',
  surcharge: 'kinds.surcharge',
  other: 'kinds.other',
};

const RECONCILE_KEYS: Record<string, string> = {
  overpaid: 'review.state.overpaid',
  mismatch: 'review.state.mismatch',
  credit_loss: 'review.state.creditLoss',
};

/** Exact reasons first, then the families (`unknown_outcome:*`, `rejected:*`, `doc:*`). */
function reasonKey(reason: string | null): string {
  if (reason === null) return 'attention.reason.unknown';
  const exact: Record<string, string> = {
    duplicate_found: 'attention.reason.duplicateFound',
    sum_mismatch: 'attention.reason.sumMismatch',
    invoice_changed: 'attention.reason.invoiceChanged',
    retries_exhausted: 'attention.reason.retriesExhausted',
    verify_failed: 'attention.reason.verifyFailed',
    missing_in_netvisor: 'attention.reason.missingInNetvisor',
    'doc:vat_code_missing': 'attention.reason.vatCodeMissing',
    'doc:product_missing': 'attention.reason.productMissing',
    'doc:due_date_missing': 'attention.reason.dueDateMissing',
    'doc:recipient_missing': 'attention.reason.recipientMissing',
    'doc:recipient_unidentified': 'attention.reason.recipientMissing',
    'doc:field_too_long': 'attention.reason.fieldTooLong',
    'doc:amounts_inconsistent': 'attention.reason.amountsInconsistent',
    'rejected:key_in_use': 'attention.reason.keyInUse',
  };
  const direct = exact[reason];
  if (direct !== undefined) return direct;
  if (reason.startsWith('unknown_outcome:')) return 'attention.reason.unknownOutcome';
  if (reason.startsWith('rejected:')) return 'attention.reason.rejected';
  if (reason.startsWith('doc:')) return 'attention.reason.documentInvalid';
  return 'attention.reason.unknown';
}

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

function statusChip(settings: NetvisorSettings, blocked: boolean): { color: 'success' | 'warning' | 'error' | 'default'; labelKey: string } {
  if (!settings.available) return { color: 'default', labelKey: 'status.unavailable' };
  if (blocked) return { color: 'warning', labelKey: 'status.blocked' };
  if (settings.ready) return { color: 'success', labelKey: 'status.on' };
  if (settings.lastTest?.success === false) return { color: 'error', labelKey: 'status.testFailed' };
  if (settings.hasCustomerKey) return { color: 'warning', labelKey: settings.lastTest?.success === true ? 'status.off' : 'status.untested' };
  return { color: 'default', labelKey: 'status.notConfigured' };
}

interface VatRow {
  rate: string;
  code: string;
}

function vatRowsOf(vatCodes: Record<string, string>): VatRow[] {
  return Object.entries(vatCodes)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([rate, code]) => ({ rate: rate.replace('.', ','), code }));
}

/** The canonical rate key (`25,5` → `25.5`), or null when the row is not a valid rate. */
function rateKey(rate: string): string | null {
  const trimmed = rate.trim();
  if (!VAT_RATE_PATTERN.test(trimmed)) return null;
  return String(Number(trimmed.replace(',', '.')));
}

type ResolveDialog =
  | { kind: 'retry-unknown'; item: NetvisorAttentionItem }
  | { kind: 'bind'; item: NetvisorAttentionItem }
  | { kind: 'abandon'; item: NetvisorAttentionItem };

export function NetvisorSettingsTab() {
  const { t } = useTranslation('netvisor');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useNetvisorIntegration(true);
  const saveCredentials = useSaveNetvisorCredentials();
  const saveSettings = useSaveNetvisorSettings();
  const test = useTestNetvisorCredentials();
  const setEnabled = useSetNetvisorEnabled();
  const remove = useDeleteNetvisorCredentials();
  const resolve = useResolveNetvisorItem();

  const [customerId, setCustomerId] = useState('');
  const [customerKey, setCustomerKey] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [language, setLanguage] = useState<NetvisorLanguage>('FI');
  const [productGroup, setProductGroup] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState<NetvisorInvoiceStatus>('open');
  const [updateCustomers, setUpdateCustomers] = useState(false);
  const [vatRows, setVatRows] = useState<VatRow[]>([]);
  const [productKeys, setProductKeys] = useState<Record<NetvisorLineKind, string>>({ labor: '', part: '', service: '', surcharge: '', other: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dialog, setDialog] = useState<ResolveDialog | null>(null);
  const [bindKey, setBindKey] = useState('');
  const [confirmedAbsent, setConfirmedAbsent] = useState(false);

  const settings = query.data?.settings;
  useEffect(() => {
    if (!settings) return;
    setCustomerId('');
    setCustomerKey('');
    setOrganisationId(settings.organisationId ?? '');
    setLanguage(settings.language);
    setProductGroup(settings.productGroup ?? '');
    setInvoiceStatus(settings.invoiceStatus);
    setUpdateCustomers(settings.updateExistingCustomers);
    setVatRows(vatRowsOf(settings.vatCodes));
    setProductKeys({
      labor: settings.products.labor?.netvisorKey ?? '',
      part: settings.products.part?.netvisorKey ?? '',
      service: settings.products.service?.netvisorKey ?? '',
      surcharge: settings.products.surcharge?.netvisorKey ?? '',
      other: settings.products.other?.netvisorKey ?? '',
    });
  }, [settings]);

  const vatInvalid = useMemo(
    () => vatRows.some((row) => (row.rate.trim() !== '' || row.code.trim() !== '') && (rateKey(row.rate) === null || !VAT_CODE_PATTERN.test(row.code.trim()))),
    [vatRows],
  );
  const keysInvalid = NETVISOR_LINE_KINDS.some((kind) => productKeys[kind].trim() !== '' && !NETVISOR_KEY_PATTERN.test(productKeys[kind].trim()));

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !query.data || !settings) {
    return <Alert severity="error">{t('loadFailed')}</Alert>;
  }

  const { summary, outboundBlocked } = query.data;
  const orgTrimmed = organisationId.trim();
  const orgInvalid = orgTrimmed !== '' && !ORGANISATION_PATTERN.test(orgTrimmed);
  const hasUser = settings.customerIdMasked !== null || customerId.trim() !== '';
  const hasKey = settings.hasCustomerKey || customerKey.trim() !== '';
  const hasOrg = ORGANISATION_PATTERN.test(orgTrimmed);

  const showError = (error: unknown, fallbackKey: string) => {
    const known = ERROR_KEYS[errorCode(error) ?? ''];
    notify.error(tc('error'), known ? t(known) : describeApiError(error, tc, { fallback: t(fallbackKey) }));
  };

  const canSaveCredentials = settings.available && settings.encryptionAvailable && hasUser && hasKey && hasOrg && !saveCredentials.isPending;
  const handleSaveCredentials = async () => {
    if (!canSaveCredentials) return;
    try {
      await saveCredentials.mutateAsync({
        ...(customerId.trim() !== '' ? { customerId: customerId.trim() } : {}),
        ...(customerKey.trim() !== '' ? { customerKey: customerKey.trim() } : {}),
        organisationId: orgTrimmed,
        language,
      });
      setCustomerKey('');
      notify.success(t('saved'), t('savedDesc'));
    } catch (error) {
      showError(error, 'errors.saveFailed');
    }
  };

  const canTest = settings.available && settings.encryptionAvailable && settings.hasCustomerKey && !test.isPending && !saveCredentials.isPending;
  const handleTest = async () => {
    if (!canTest) return;
    try {
      const result = await test.mutateAsync(undefined);
      if (result.success) notify.success(t('testSuccess'), t('testSuccessDesc'));
      else notify.error(tc('error'), t(LAST_TEST_ERROR_KEYS[result.settings.lastTest?.error ?? ''] ?? 'errors.testFailed'));
    } catch (error) {
      showError(error, 'errors.testFailed');
    }
  };

  const buildProducts = (): NetvisorSettingsInput['products'] => {
    const products: NonNullable<NetvisorSettingsInput['products']> = {};
    for (const kind of NETVISOR_LINE_KINDS) {
      const key = productKeys[kind].trim();
      const existing: NetvisorProductConfig = { ...(settings.products[kind] ?? {}) };
      if (key !== '') {
        products[kind] = { ...existing, netvisorKey: key };
      } else if (existing.netvisorKey !== undefined) {
        delete existing.netvisorKey;
        products[kind] = Object.keys(existing).length > 0 ? existing : null;
      }
    }
    return products;
  };

  const canSaveSettings = !vatInvalid && !keysInvalid && !saveSettings.isPending;
  const handleSaveSettings = async () => {
    if (!canSaveSettings) return;
    const vatCodes: Record<string, string> = {};
    for (const row of vatRows) {
      const key = rateKey(row.rate);
      if (key !== null && VAT_CODE_PATTERN.test(row.code.trim())) vatCodes[key] = row.code.trim().toUpperCase();
    }
    try {
      await saveSettings.mutateAsync({
        invoiceStatus,
        productGroup: productGroup.trim() === '' ? null : productGroup.trim(),
        products: buildProducts(),
        vatCodes,
        updateExistingCustomers: updateCustomers,
      });
      notify.success(t('mappingSaved'), t('mappingSavedDesc'));
    } catch (error) {
      showError(error, 'errors.saveFailed');
    }
  };

  const handleToggle = async (next: boolean) => {
    try {
      await setEnabled.mutateAsync(next);
      notify.success(next ? t('enabledOn') : t('enabledOff'), next ? t('enabledOnDesc') : t('enabledOffDesc'));
    } catch (error) {
      showError(error, 'errors.toggleFailed');
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(undefined);
      setConfirmDelete(false);
      notify.success(t('deleted'), t('deletedDesc'));
    } catch (error) {
      showError(error, 'errors.deleteFailed');
    }
  };

  const closeDialog = () => {
    setDialog(null);
    setBindKey('');
    setConfirmedAbsent(false);
  };

  const submitDecision = async (input: { item: NetvisorAttentionItem; decision: 'retry' | 'bind' | 'abandon'; netvisorKey?: string; confirmNotInNetvisor?: boolean }) => {
    try {
      await resolve.mutateAsync({
        type: input.item.type,
        id: input.item.id,
        decision: input.decision,
        ...(input.netvisorKey !== undefined ? { netvisorKey: input.netvisorKey } : {}),
        ...(input.confirmNotInNetvisor !== undefined ? { confirmNotInNetvisor: input.confirmNotInNetvisor } : {}),
      });
      closeDialog();
      notify.success(t('attention.resolved'), t(`attention.resolvedDesc.${input.decision}`));
    } catch (error) {
      showError(error, 'errors.resolveFailed');
    }
  };

  const isUnknown = (item: NetvisorAttentionItem) => item.reason?.startsWith('unknown_outcome:') === true;
  const itemLabel = (item: NetvisorAttentionItem) => {
    if (item.type === 'invoice') return t('attention.invoice', { number: item.label });
    if (item.type === 'customer') return t('attention.customer');
    return t('attention.product', { kind: t(KIND_KEYS[item.label as NetvisorLineKind] ?? 'kinds.other') });
  };

  const chip = statusChip(settings, outboundBlocked);
  const lastTest = settings.lastTest;
  const canSwitch = settings.enabled || (settings.missing.length === 0 && settings.available && !outboundBlocked);

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">{t('title')}</Typography>
          <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('intro')}
        </Typography>
      </Box>

      {!settings.available && <Alert severity="info">{t('unavailable')}</Alert>}
      {!settings.encryptionAvailable && <Alert severity="warning">{t('encryptionMissing')}</Alert>}
      {outboundBlocked && <Alert severity="warning">{t('outboundBlocked')}</Alert>}
      {lastTest?.success === false && <Alert severity="error">{t(LAST_TEST_ERROR_KEYS[lastTest.error ?? ''] ?? 'errors.testFailed')}</Alert>}
      {settings.ready && !outboundBlocked && (
        <Alert severity="success">{settings.exportSince !== null ? t('readyHint', { since: new Date(settings.exportSince).toLocaleDateString() }) : t('readyHintNoDate')}</Alert>
      )}

      <Stack spacing={2}>
        <Typography variant="subtitle1">{t('credentials.title')}</Typography>
        <TextField
          label={t('customerId')}
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          placeholder={settings.customerIdMasked ?? ''}
          helperText={settings.customerIdMasked !== null ? t('customerIdStoredHint', { masked: settings.customerIdMasked }) : t('customerIdHint')}
          autoComplete="off"
          fullWidth
        />
        <TextField
          label={t('customerKey')}
          type="password"
          value={customerKey}
          onChange={(e) => setCustomerKey(e.target.value)}
          helperText={settings.hasCustomerKey ? t('customerKeySetHint') : t('customerKeyHint')}
          autoComplete="new-password"
          fullWidth
        />
        <TextField
          label={t('organisationId')}
          value={organisationId}
          onChange={(e) => setOrganisationId(e.target.value)}
          error={orgInvalid}
          helperText={orgInvalid ? t('organisationIdInvalid') : t('organisationIdHint')}
          autoComplete="off"
          fullWidth
        />
        <TextField select label={t('language')} value={language} onChange={(e) => setLanguage(e.target.value as NetvisorLanguage)} helperText={t('languageHint')} fullWidth>
          {LANGUAGES.map((code) => (
            <MenuItem key={code} value={code}>
              {LANGUAGE_NAMES[code]}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={saveCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSaveCredentials()}
            disabled={!canSaveCredentials}
          >
            {t('save')}
          </Button>
          <Button variant="outlined" startIcon={test.isPending ? <CircularProgress size={16} color="inherit" /> : undefined} onClick={() => void handleTest()} disabled={!canTest}>
            {t('test')}
          </Button>
          <Button color="error" onClick={() => setConfirmDelete(true)} disabled={!settings.hasCustomerKey || remove.isPending}>
            {t('delete')}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography variant="subtitle1">{t('mapping.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('mapping.intro')}
        </Typography>

        <Typography variant="subtitle2">{t('vat.title')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('vat.hint')}
        </Typography>
        {vatRows.map((row, index) => {
          const rateBad = row.rate.trim() !== '' && rateKey(row.rate) === null;
          const codeBad = row.code.trim() !== '' && !VAT_CODE_PATTERN.test(row.code.trim());
          return (
            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label={t('vat.rate')}
                value={row.rate}
                onChange={(e) => setVatRows((rows) => rows.map((r, i) => (i === index ? { ...r, rate: e.target.value } : r)))}
                error={rateBad}
                helperText={rateBad ? t('vat.rateInvalid') : ' '}
                sx={{ width: 140 }}
              />
              <TextField
                label={t('vat.code')}
                value={row.code}
                onChange={(e) => setVatRows((rows) => rows.map((r, i) => (i === index ? { ...r, code: e.target.value } : r)))}
                error={codeBad}
                helperText={codeBad ? t('vat.codeInvalid') : ' '}
                sx={{ flex: 1 }}
              />
              <Tooltip title={t('vat.remove')}>
                <IconButton aria-label={t('vat.remove')} onClick={() => setVatRows((rows) => rows.filter((_r, i) => i !== index))}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
        <Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setVatRows((rows) => [...rows, { rate: '', code: '' }])}>
            {t('vat.add')}
          </Button>
        </Box>

        <TextField
          label={t('productGroup')}
          value={productGroup}
          onChange={(e) => setProductGroup(e.target.value)}
          helperText={t('productGroupHint')}
          fullWidth
        />

        <Typography variant="subtitle2">{t('products.title')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('products.hint')}
        </Typography>
        {NETVISOR_LINE_KINDS.map((kind) => {
          const bad = productKeys[kind].trim() !== '' && !NETVISOR_KEY_PATTERN.test(productKeys[kind].trim());
          return (
            <TextField
              key={kind}
              label={t('products.existing', { kind: t(KIND_KEYS[kind]) })}
              value={productKeys[kind]}
              onChange={(e) => setProductKeys((keys) => ({ ...keys, [kind]: e.target.value }))}
              error={bad}
              helperText={bad ? t('products.keyInvalid') : ' '}
              fullWidth
            />
          );
        })}

        <TextField select label={t('invoiceStatus.label')} value={invoiceStatus} onChange={(e) => setInvoiceStatus(e.target.value as NetvisorInvoiceStatus)} helperText={t(`invoiceStatus.hint.${invoiceStatus}`)} fullWidth>
          {INVOICE_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {t(`invoiceStatus.${status}`)}
            </MenuItem>
          ))}
        </TextField>
        <Box>
          <FormControlLabel
            control={<Switch checked={updateCustomers} onChange={(_event, checked) => setUpdateCustomers(checked)} />}
            label={t('updateCustomers.label')}
          />
          <Typography variant="caption" color="text.secondary" component="p">
            {t('updateCustomers.hint')}
          </Typography>
        </Box>
        <Box>
          <Button variant="contained" startIcon={saveSettings.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => void handleSaveSettings()} disabled={!canSaveSettings}>
            {t('mapping.save')}
          </Button>
        </Box>
      </Stack>

      <Box>
        <FormControlLabel
          control={<Switch checked={settings.enabled} onChange={(_event, checked) => void handleToggle(checked)} disabled={!canSwitch || setEnabled.isPending} />}
          label={t('enabledLabel')}
        />
        {settings.missing.length > 0 ? (
          <Typography variant="caption" color="text.secondary" component="p">
            {t('enabledNeeds', { missing: settings.missing.map((key) => t(MISSING_KEYS[key] ?? 'missing.credentials')).join(', ') })}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" component="p">
            {t('enabledHint')}
          </Typography>
        )}
      </Box>

      {settings.enabled && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('summary.title')}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(['queued', 'posted', 'verified', 'attention', 'cancelled'] as const).map((status) => (
              <Chip key={status} size="small" variant="outlined" label={t(`summary.invoices.${status}`, { count: summary.invoices[status] ?? 0 })} />
            ))}
          </Stack>
        </Stack>
      )}

      {summary.attentionCount > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('attention.title', { count: summary.attentionCount })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attention.intro')}
          </Typography>
          <List dense disablePadding>
            {summary.attention.map((item) => (
              <ListItem key={`${item.type}:${item.id}`} divider sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <ListItemText primary={itemLabel(item)} secondary={t(reasonKey(item.reason))} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {!item.hasNetvisorKey && item.reason !== 'duplicate_found' && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={resolve.isPending}
                      onClick={() => (isUnknown(item) ? setDialog({ kind: 'retry-unknown', item }) : void submitDecision({ item, decision: 'retry' }))}
                    >
                      {t('attention.retry')}
                    </Button>
                  )}
                  {!item.hasNetvisorKey && (
                    <Button size="small" variant="outlined" disabled={resolve.isPending} onClick={() => setDialog({ kind: 'bind', item })}>
                      {t('attention.bind')}
                    </Button>
                  )}
                  {item.type === 'invoice' && (
                    <Button size="small" color="error" disabled={resolve.isPending} onClick={() => setDialog({ kind: 'abandon', item })}>
                      {t('attention.abandon')}
                    </Button>
                  )}
                </Stack>
              </ListItem>
            ))}
          </List>
        </Stack>
      )}

      {summary.review.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">{t('review.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('review.intro')}
          </Typography>
          <List dense disablePadding>
            {summary.review.map((item) => (
              <ListItem key={item.exportId} divider>
                <ListItemText
                  primary={t('review.item', { number: item.invoiceNumber, state: t(RECONCILE_KEYS[item.reconcileState] ?? 'review.state.mismatch') })}
                  secondary={t('review.amounts', {
                    total: formatCentsForDisplay(item.totalCents),
                    paid: formatCentsForDisplay(item.paidCents),
                    local: item.localPaidCents === null ? t('review.noLocal') : formatCentsForDisplay(item.localPaidCents),
                  })}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('deleteTitle')}
        message={t('deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
        loading={remove.isPending}
      />

      <ResponsiveDialog open={dialog !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        {dialog?.kind === 'retry-unknown' && (
          <>
            <DialogTitle>{t('retryUnknown.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>{t('retryUnknown.message')}</DialogContentText>
              <FormControlLabel control={<Checkbox checked={confirmedAbsent} onChange={(_e, checked) => setConfirmedAbsent(checked)} />} label={t('retryUnknown.confirm')} />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{tc('cancel')}</Button>
              <Button variant="contained" disabled={!confirmedAbsent || resolve.isPending} onClick={() => void submitDecision({ item: dialog.item, decision: 'retry', confirmNotInNetvisor: true })}>
                {t('attention.retry')}
              </Button>
            </DialogActions>
          </>
        )}
        {dialog?.kind === 'bind' && (
          <>
            <DialogTitle>{t('bind.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>{t(dialog.item.type === 'invoice' ? 'bind.messageInvoice' : 'bind.message')}</DialogContentText>
              <TextField
                autoFocus
                label={t('bind.key')}
                value={bindKey}
                onChange={(e) => setBindKey(e.target.value)}
                error={bindKey.trim() !== '' && !NETVISOR_KEY_PATTERN.test(bindKey.trim())}
                helperText={t('bind.keyHint')}
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{tc('cancel')}</Button>
              <Button variant="contained" disabled={!NETVISOR_KEY_PATTERN.test(bindKey.trim()) || resolve.isPending} onClick={() => void submitDecision({ item: dialog.item, decision: 'bind', netvisorKey: bindKey.trim() })}>
                {t('attention.bind')}
              </Button>
            </DialogActions>
          </>
        )}
        {dialog?.kind === 'abandon' && (
          <>
            <DialogTitle>{t('abandon.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('abandon.message')}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{tc('cancel')}</Button>
              <Button color="error" variant="contained" disabled={resolve.isPending} onClick={() => void submitDecision({ item: dialog.item, decision: 'abandon' })}>
                {t('attention.abandon')}
              </Button>
            </DialogActions>
          </>
        )}
      </ResponsiveDialog>
    </Stack>
  );
}
